import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Loader2, Search } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface GlobalLeadGenSettings {
  allowUnverified: boolean;
  emailFinderEnabled: boolean;
  apolloEnabled: boolean;
  recurringEnabled: boolean;
}

function ToggleRow({
  id,
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-t pt-4 first:border-t-0 first:pt-0">
      <div className="space-y-0.5">
        <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        data-testid={`switch-${id}`}
      />
    </div>
  );
}

export function GlobalLeadGenSettingsContent() {
  const { toast } = useToast();

  const { data: settings, isLoading } = useQuery<GlobalLeadGenSettings>({
    queryKey: ["/api/platform/settings/lead-gen-ai"],
    queryFn: async () => {
      const res = await fetch("/api/platform/settings/lead-gen-ai", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load global AI Lead Search settings");
      return res.json();
    },
  });

  const patchMutation = useMutation({
    mutationFn: async (patch: Partial<GlobalLeadGenSettings>) => {
      const res = await apiRequest("PATCH", "/api/platform/settings/lead-gen-ai", patch);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Saved", description: "Global AI Lead Search settings updated." });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/settings/lead-gen-ai"] });
      queryClient.invalidateQueries({ queryKey: ["/api/lead-gen/settings"] });
    },
    onError: (e: any) =>
      toast({ title: "Save failed", description: e?.message, variant: "destructive" }),
  });

  const toggle = (field: keyof GlobalLeadGenSettings) => (v: boolean) =>
    patchMutation.mutate({ [field]: v });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Search className="w-5 h-5 text-primary" />
        <div>
          <h1 className="text-xl font-semibold">AI Lead Search</h1>
          <p className="text-xs text-muted-foreground">
            Global settings that apply to every workspace. The per-workspace enable/disable toggle stays
            in each workspace's AI Settings → General panel.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Feature toggles</CardTitle>
          <CardDescription>
            Turn off a feature globally to disable it across all workspaces at once,
            regardless of individual workspace settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading || !settings ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <ToggleRow
                id="global-allow-unverified"
                label="Allow unverified results"
                description="When off, only candidates that match a Google Places record are added to any workspace's sandbox. Unverified results are always free."
                checked={settings.allowUnverified}
                disabled={patchMutation.isPending}
                onChange={toggle("allowUnverified")}
              />
              <ToggleRow
                id="global-email-finder"
                label="Email finder"
                description="Per-row scrape of a venue's website to extract a booking contact email (1 credit per find)."
                checked={settings.emailFinderEnabled}
                disabled={patchMutation.isPending}
                onChange={toggle("emailFinderEnabled")}
              />
              <ToggleRow
                id="global-apollo"
                label="Apollo lookups"
                description="Per-row enrichment via Apollo.io for talent buyer / GM contact data including LinkedIn (1 credit per lookup). The Apollo API key is configured separately under Platform Settings → Apollo."
                checked={settings.apolloEnabled}
                disabled={patchMutation.isPending}
                onChange={toggle("apolloEnabled")}
              />
              <ToggleRow
                id="global-recurring"
                label="Recurring saved searches"
                description="Allow saved AI searches to re-run monthly and email a digest of new finds to the search creator."
                checked={settings.recurringEnabled}
                disabled={patchMutation.isPending}
                onChange={toggle("recurringEnabled")}
              />
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Credit cost reference</CardTitle>
          <CardDescription>
            These costs are charged from each workspace's Lead Gen credit pool per operation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left pb-2 font-medium text-muted-foreground">Operation</th>
                <th className="text-right pb-2 font-medium text-muted-foreground">Credits</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr>
                <td className="py-2">Verified result (Google Places match)</td>
                <td className="py-2 text-right font-medium">2</td>
              </tr>
              <tr>
                <td className="py-2">Unverified result</td>
                <td className="py-2 text-right font-medium text-muted-foreground">Free</td>
              </tr>
              <tr>
                <td className="py-2">Email finder (per found email)</td>
                <td className="py-2 text-right font-medium">1</td>
              </tr>
              <tr>
                <td className="py-2">Apollo lookup (per match)</td>
                <td className="py-2 text-right font-medium">1</td>
              </tr>
              <tr>
                <td className="py-2">AI Enrich (capacity, contact role, venue type)</td>
                <td className="py-2 text-right font-medium">1</td>
              </tr>
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground mt-3">
            Scout AI discovery runs on Claude Sonnet as the fixed default.
            There is no per-workspace model selector.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
